/**
 * Shoorah admin design tokens — colour coding for the whole panel.
 *
 * Nothing outside this file should hard-code a chart hex or a badge colour.
 * A feature maps its own vocabulary ("Advanced", "paused", "Excellent") onto a
 * *tone* name here, and gets both the chart colour and the chip classes back,
 * so a chart, its legend and the table underneath it can never drift apart.
 *
 * Changing the guideline = changing this file.
 */

// Base palette — the full wheel, blue and purple included.
//
// The monochrome rule is about chrome, not data: buttons, hovers, focus rings
// and active states carry no hue, so that a chart's colour means something
// when you see it. Stripping the charts as well would have been the opposite
// of the point, and for a while this palette did exactly that.
//
// Entries are separated by lightness as well as hue — `blue` against `indigo`,
// `green` against `moss` — so a five-slice donut still reads as five slices. Each is legible on both the white card and the near-black one.
export const CHART_COLORS = {
  brand: '#2FC9CD', // primary series
  brandSoft: '#9BE3E5',
  indigo: '#7B72E9',
  blue: '#3B9EF5',
  teal: '#1F9EA1',
  pink: '#E8467C',
  amber: '#FFC53D',
  orange: '#F0913C',
  green: '#46A758',
  greenDeep: '#2F9E77',
  moss: '#6E9B3A',
  red: '#E5484D',
  gray: '#9CA3AF'
};

// Order follows the five-ring card in the design (pink → indigo → blue → teal
// → orange). Use it for any series whose categories have no inherent meaning
// (top 10 lists, goal breakdowns) so ranking stays visually consistent across
// pages.
export const CHART_PALETTE = [
  CHART_COLORS.pink,
  CHART_COLORS.indigo,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.orange
];

export const paletteAt = (index) => CHART_PALETTE[index % CHART_PALETTE.length];

/**
 * Semantic tones. `color` drives charts and swatches, `chip` drives badges —
 * always the same pairing, which is what makes a chart and its table read as
 * one thing. Chip classes follow the People Plan convention
 * (see B2B/CompanyAdmin/PeoplePlan/components/roadMapStatus.js).
 */
export const TONES = {
  brand: { color: CHART_COLORS.brand, chip: 'bg-shoorah-lightIndigo text-shoorah-primary' },
  info: { color: CHART_COLORS.blue, chip: 'bg-amber-100 text-amber-800' },
  success: { color: CHART_COLORS.green, chip: 'bg-green-100 text-green-700' },
  warning: { color: CHART_COLORS.amber, chip: 'bg-yellow-100 text-yellow-700' },
  danger: { color: CHART_COLORS.red, chip: 'bg-red-100 text-red-700' },
  accent: { color: CHART_COLORS.pink, chip: 'bg-pink-100 text-pink-700' },
  teal: { color: CHART_COLORS.teal, chip: 'bg-teal-100 text-teal-700' },
  orange: { color: CHART_COLORS.orange, chip: 'bg-orange-100 text-orange-700' },
  neutral: { color: CHART_COLORS.gray, chip: 'bg-gray-100 text-gray-700' }
};

const toneOf = (name) => TONES[name] || TONES.neutral;

export const toneColor = (name) => toneOf(name).color;
export const toneChip = (name) => toneOf(name).chip;
/** Tone names → chart `colors` array, for a series ordered by bucket. */
export const toneColors = (names = []) => names.map(toneColor);

/**
 * Builds a `label → tone name` lookup that matches loosely, so "Advanced",
 * "advanced " and "ADVANCED" land in the same bucket.
 */
export const toneLookup = (map, fallback = 'neutral') => {
  const normalised = Object.entries(map).reduce((acc, [key, tone]) => {
    acc[key.toLowerCase()] = tone;
    return acc;
  }, {});
  return (label = '') => normalised[`${label}`.trim().toLowerCase()] || fallback;
};

/**
 * Chart chrome, per theme.
 *
 * Apex draws to SVG with inline attributes, so it needs real hex values — a
 * Tailwind class never reaches it. These are the four it asks for, and they
 * are here rather than in the components so the two themes stay defined side
 * by side. Read them through `useChartTheme()` in Charts.jsx, which picks the
 * set and re-renders when the toggle flips.
 *
 * `surface` is the card the chart sits on: donut gaps are drawn in it, so a
 * slice separator reads as a gap rather than as a white line on a dark card.
 */
export const CHART_THEME = {
  light: { surface: '#FFFFFF', label: '#6B7280', value: '#0B1D2E', grid: '#E7E8EA' },
  dark: { surface: '#26282C', label: '#9CA3AF', value: '#FFFFFF', grid: '#343638' }
};

// ---------------------------------------------------------------------------
// ApexCharts defaults. Spread these instead of repeating chart config.
// ---------------------------------------------------------------------------

export const CHART_BASE = { toolbar: { show: false }, fontFamily: 'inherit' };

export const CHART_GRID_BORDER = '#E7E8EA'; // shoorah-borderGray

/** Horizontal rules only — the default for column, area and line charts. */
export const CHART_GRID = { borderColor: CHART_GRID_BORDER, strokeDashArray: 4 };

/** For horizontal bar charts, where vertical rules fight the bars. */
export const CHART_GRID_NO_X = {
  borderColor: CHART_GRID_BORDER,
  xaxis: { lines: { show: false } }
};

export const CHART_LEGEND = {
  position: 'bottom',
  horizontalAlign: 'center',
  markers: { radius: 12 },
  fontSize: '13px'
};

/** Soft top-down gradient used by every area chart in the panel. */
export const AREA_FILL = {
  type: 'gradient',
  gradient: { shadeIntensity: 0.4, opacityFrom: 0.4, opacityTo: 0.05 }
};

/**
 * Donut config, which is otherwise copy-pasted per chart.
 * `unit` names what a slice counts ("logs", "habits", "users").
 */
export const donutOptions = ({ labels = [], colors = CHART_PALETTE, unit = '' }) => ({
  chart: { ...CHART_BASE, type: 'donut' },
  labels,
  colors,
  stroke: { width: 0 },
  legend: CHART_LEGEND,
  dataLabels: { formatter: (val) => `${Math.round(val)}%` },
  tooltip: { y: { formatter: (val) => `${val.toLocaleString()}${unit ? ` ${unit}` : ''}` } }
});
